'use client';

import _extends from "@babel/runtime/helpers/esm/extends";
import _objectWithoutPropertiesLoose from "@babel/runtime/helpers/esm/objectWithoutPropertiesLoose";
const _excluded = ["className", "render", "style", "start", "end", "eventId", "occurrenceKey", "renderDragPreview", "id", "isDraggable", "nativeButton"];
import * as React from 'react';
import { useStableCallback } from '@base-ui/utils/useStableCallback';
import { useStore } from '@base-ui/utils/store/useStore';
import { useId } from '@base-ui/utils/useId';
import { useButton } from "../../base-ui-copy/utils/useButton.mjs";
import { useRenderElement } from "../../base-ui-copy/utils/useRenderElement.mjs";
import { useDraggableEvent } from "../../internals/utils/useDraggableEvent.mjs";
import { useAdapterContext } from "../../use-adapter-context/index.mjs";
import { useCalendarGridDayRowContext } from "../day-row/CalendarGridDayRowContext.mjs";
import { schedulerEventSelectors, schedulerOccurrencePlaceholderSelectors } from "../../scheduler-selectors/index.mjs";
import { getCalendarGridHeaderCellId } from "../../internals/utils/accessibility-utils.mjs";
import { CalendarGridDayEventContext } from "./CalendarGridDayEventContext.mjs";
import { useEventCalendarStoreContext } from "../../use-event-calendar-store-context/index.mjs";
import { useCalendarGridDayCellContext } from "../day-cell/CalendarGridDayCellContext.mjs";
import { useCalendarGridRootContext } from "../root/CalendarGridRootContext.mjs";
import { generateOccurrenceFromEvent } from "../../internals/utils/event-utils.mjs";
import { jsxs as _jsxs } from "react/jsx-runtime";
const overflowStateAttributesMapping = {
  startingBeforeEdge: value => value ? {
    'data-starting-before-edge': ''
  } : null,
  endingAfterEdge: value => value ? {
    'data-ending-after-edge': ''
  } : null
};
export const CalendarGridDayEvent = /*#__PURE__*/React.forwardRef(function CalendarGridDayEvent(componentProps, forwardedRef) {
  const {
      // Rendering props

      // Internal props
      start,
      end,
      eventId,
      occurrenceKey,
      renderDragPreview,
      id: idProp,
      isDraggable = false,
      nativeButton = false
      // Props forwarded to the DOM element
    } = componentProps,
    elementProps = _objectWithoutPropertiesLoose(componentProps, _excluded);

  // TODO: Expose a real `interactive` prop
  // to control whether the event should behave like a button
  const isInteractive = true;

  // Context hooks
  const adapter = useAdapterContext();
  const store = useEventCalendarStoreContext();
  const {
    id: rootId
  } = useCalendarGridRootContext();
  const {
    start: rowStart,
    end: rowEnd
  } = useCalendarGridDayRowContext();
  const {
    index: cellIndex,
    hasFocus: cellHasFocus
  } = useCalendarGridDayCellContext();

  // Ref hooks
  const ref = React.useRef(null);

  // Selector hooks
  const hasPlaceholder = useStore(store, schedulerOccurrencePlaceholderSelectors.isDefined);

  // State hooks
  const id = useId(idProp);

  // Feature hooks
  const getDraggedDay = useStableCallback(input => {
    if (!ref.current) {
      return start.value;
    }
    const eventStartInRow = adapter.isBefore(start.value, rowStart) ? rowStart : start.value;
    const eventEndInRow = adapter.isAfter(end.value, rowEnd) ? rowEnd : end.value;
    const eventDayLengthInRow = adapter.differenceInDays(eventEndInRow, eventStartInRow) + 1;
    const clientX = input.clientX;
    const elementPosition = ref.current.getBoundingClientRect();
    const positionX = (clientX - elementPosition.x) / ref.current.offsetWidth;
    return adapter.startOfDay(adapter.addDays(eventStartInRow, Math.ceil(positionX * eventDayLengthInRow) - 1));
  });
  const firstEventOfSeries = schedulerEventSelectors.processedEvent(store.state, eventId);
  const originalOccurrence = generateOccurrenceFromEvent({
    event: firstEventOfSeries,
    eventId,
    occurrenceKey,
    start,
    end
  });
  const getSharedDragData = useStableCallback(() => ({
    eventId,
    occurrenceKey,
    originalOccurrence,
    start: start.value,
    end: end.value
  }));
  const getDragData = useStableCallback(input => _extends({}, getSharedDragData(input), {
    source: 'CalendarGridDayEvent',
    draggedDay: getDraggedDay(input)
  }));
  const {
    state,
    preview,
    contextValue: draggableEventContextValue
  } = useDraggableEvent({
    ref,
    start,
    end,
    occurrenceKey,
    eventId,
    isDraggable,
    renderDragPreview,
    getDragData,
    collectionStart: rowStart,
    collectionEnd: rowEnd
  });
  const startingBeforeEdge = draggableEventContextValue.doesEventStartBeforeCollectionStart;
  const endingAfterEdge = draggableEventContextValue.doesEventEndAfterCollectionEnd;
  const mergedState = _extends({}, state, {
    startingBeforeEdge,
    endingAfterEdge
  });
  const {
    getButtonProps,
    buttonRef
  } = useButton({
    disabled: !isInteractive,
    native: nativeButton,
    tabIndex: cellHasFocus ? 0 : -1
  });

  // Rendering hooks

  const columnHeaderId = getCalendarGridHeaderCellId(rootId, cellIndex);
  const contextValue = React.useMemo(() => _extends({}, draggableEventContextValue, {
    getSharedDragData
  }), [draggableEventContextValue, getSharedDragData]);
  const element = useRenderElement('div', componentProps, {
    state: mergedState,
    ref: [forwardedRef, buttonRef, ref],
    props: [elementProps, {
      id,
      'aria-labelledby': `${columnHeaderId} ${id}`,
      style: hasPlaceholder ? {
        pointerEvents: 'none'
      } : undefined
    }, getButtonProps],
    stateAttributesMapping: overflowStateAttributesMapping
  });
  return /*#__PURE__*/_jsxs(CalendarGridDayEventContext.Provider, {
    value: contextValue,
    children: [element, preview.element]
  });
});
if (process.env.NODE_ENV !== "production") CalendarGridDayEvent.displayName = "CalendarGridDayEvent";